import { useEffect, type ComponentType } from 'react'
import type { TranslationKey } from '@/modules/i18n'
import { useTar } from '@/modules/i18n'
import { isSupportedLanguage } from '@/modules/i18n/supportedLanguages'
import { ExternalAssets, ExternalURLs, ORGANIZATION_EMAIL } from '@/types/externalURLs'
import { localizedUrlsForPath, isEnglishOnlyPath } from './prerenderedPaths'
import { DEFAULT_OG_IMAGE, getOgImage } from './ogImage'
import { dedupeTitleTags } from './dedupeTitleTags'

const SITE_URL = ExternalURLs.Site
const SITE_NAME = 'XP Lab'
const DEFAULT_OG_IMAGE_WIDTH = 1200
const DEFAULT_OG_IMAGE_HEIGHT = 630

const SUPPORTED_LOCALES = ['en', 'he', 'de'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

type PublicPageSeoId =
  | 'home'
  | 'about'
  | 'faq'
  | 'forEmployers'
  | 'forEducation'
  | 'challengesAvailable'
  | 'takeHomeAssignments'
  | 'feedbackWorks'
  | 'aiJudgment'
  | 'solveAnalyticalTask'
  | 'privacyPolicy'
  | 'termsOfService'
  | 'updates'
  | 'updateDetail'

interface PublicPageSeoConfig {
  path: string
  titleKey: TranslationKey
  descriptionKey: TranslationKey
  ogType?: 'website' | 'article'
  /** Optional bespoke OG image URL. Falls back to the site default. */
  ogImage?: string
  robots?: string
  schemaType?: 'Article' | 'HowTo' | 'AboutPage'
}

const PUBLIC_PAGE_SEO_CONFIG: Record<PublicPageSeoId, PublicPageSeoConfig> = {
  home: {
    path: '/',
    titleKey: 'seo.home.title',
    descriptionKey: 'seo.home.description',
    ogType: 'website',
  },
  about: {
    path: '/about',
    titleKey: 'seo.about.title',
    descriptionKey: 'seo.about.description',
    ogType: 'website',
    schemaType: 'AboutPage',
  },
  faq: {
    path: '/faq',
    titleKey: 'seo.faq.title',
    descriptionKey: 'seo.faq.description',
    ogType: 'website',
  },
  forEmployers: {
    path: '/for-employers',
    titleKey: 'seo.forEmployers.title',
    descriptionKey: 'seo.forEmployers.description',
    ogType: 'website',
  },
  forEducation: {
    path: '/for-education',
    titleKey: 'seo.forEducation.title',
    descriptionKey: 'seo.forEducation.description',
    ogType: 'website',
  },
  challengesAvailable: {
    path: '/challenges/available',
    titleKey: 'seo.challengesAvailable.title',
    descriptionKey: 'seo.challengesAvailable.description',
    ogType: 'website',
    ogImage: ExternalAssets.ChallengesAvailableOGImage,
  },
  takeHomeAssignments: {
    path: '/take-home-assignments',
    titleKey: 'seo.takeHomeAssignments.title',
    descriptionKey: 'seo.takeHomeAssignments.description',
    ogType: 'article',
    schemaType: 'Article',
  },
  feedbackWorks: {
    path: '/xp-lab-feedback',
    titleKey: 'seo.feedbackWorks.title',
    descriptionKey: 'seo.feedbackWorks.description',
    ogType: 'article',
    schemaType: 'Article',
  },
  aiJudgment: {
    path: '/using-ai-without-outsourcing-judgment',
    titleKey: 'seo.aiJudgment.title',
    descriptionKey: 'seo.aiJudgment.description',
    ogType: 'article',
    schemaType: 'HowTo',
  },
  solveAnalyticalTask: {
    path: '/analytical-workflow',
    titleKey: 'seo.solveAnalyticalTask.title',
    descriptionKey: 'seo.solveAnalyticalTask.description',
    ogType: 'article',
    schemaType: 'HowTo',
  },
  privacyPolicy: {
    path: '/privacy-policy',
    titleKey: 'seo.privacyPolicy.title',
    descriptionKey: 'seo.privacyPolicy.description',
    ogType: 'article',
  },
  termsOfService: {
    path: '/terms-of-service',
    titleKey: 'seo.termsOfService.title',
    descriptionKey: 'seo.termsOfService.description',
    ogType: 'article',
  },
  updates: {
    path: '/updates',
    titleKey: 'seo.updates.title',
    descriptionKey: 'seo.updates.description',
    ogType: 'website',
    robots: 'noindex,follow',
  },
  updateDetail: {
    path: '/updates',
    titleKey: 'seo.updateDetail.title',
    descriptionKey: 'seo.updateDetail.description',
    ogType: 'article',
    robots: 'noindex,follow',
  },
}

function toCanonicalUrl(path: string) {
  return new URL(path, SITE_URL).toString()
}

function toOpenGraphLocale(locale: string) {
  const base = locale.split('-')[0]
  switch (base) {
    case 'he':
      return 'he_IL'
    case 'de':
      return 'de_DE'
    case 'en':
    default:
      return 'en_US'
  }
}

function getSharedStructuredData(locale: string) {
  const organizationId = `${SITE_URL}/#organization`

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: SITE_NAME,
        alternateName: ['XP-lab', 'xplab'],
        url: SITE_URL,
        logo: toCanonicalUrl('/logo.png'),
        email: ORGANIZATION_EMAIL,
        sameAs: [ExternalURLs.LinkedInPage, ExternalURLs.DiscordInvite],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        inLanguage: locale,
        publisher: {
          '@id': organizationId,
        },
      },
    ],
  }
}

function getPageSchema(
  schemaType: 'Article' | 'HowTo' | 'AboutPage',
  args: { title: string; description: string; canonicalUrl: string; locale: string }
) {
  const organizationId = `${SITE_URL}/#organization`
  const baseAuthor = { '@id': organizationId }

  if (schemaType === 'Article') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: args.title,
      description: args.description,
      mainEntityOfPage: { '@type': 'WebPage', '@id': args.canonicalUrl },
      url: args.canonicalUrl,
      inLanguage: args.locale,
      image: DEFAULT_OG_IMAGE,
      author: baseAuthor,
      publisher: baseAuthor,
    }
  }

  if (schemaType === 'HowTo') {
    return {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: args.title,
      description: args.description,
      url: args.canonicalUrl,
      inLanguage: args.locale,
      image: DEFAULT_OG_IMAGE,
    }
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: args.title,
    description: args.description,
    url: args.canonicalUrl,
    inLanguage: args.locale,
    mainEntity: { '@id': organizationId },
  }
}

export function PublicPageSeo({
  pageId,
  pathOverride,
}: {
  pageId: PublicPageSeoId
  pathOverride?: string
}) {
  const { t, locale } = useTar()
  const config = PUBLIC_PAGE_SEO_CONFIG[pageId]
  const title = t(config.titleKey)
  const description = t(config.descriptionKey)
  const ogImage = getOgImage(config)
  const path = pathOverride ?? config.path
  // en-only pages (no /$lang/... route, e.g. /challenges/available) must not
  // advertise he/de alternates — those URLs would 404.
  const enOnly = isEnglishOnlyPath(path)
  const sharedStructuredData = getSharedStructuredData(locale)
  const ogLocale = toOpenGraphLocale(locale)
  const baseLocale = (locale.split('-')[0] ?? locale) as SupportedLocale
  const alternateLocales = enOnly
    ? []
    : SUPPORTED_LOCALES.filter((l) => l !== baseLocale)
  const localizedUrls = localizedUrlsForPath(path)
  const selfUrl = isSupportedLanguage(baseLocale)
    ? localizedUrls[baseLocale]
    : localizedUrls.en
  const pageSchema = config.schemaType
    ? getPageSchema(config.schemaType, { title, description, canonicalUrl: selfUrl, locale })
    : null

  // React 19's head-hoisting does not dedupe <title>, <link rel="canonical"> or
  // <link rel="alternate"> against pre-existing head elements: on hydration of a
  // prerendered page (and during the prerender capture itself) React appends a
  // second copy alongside the one already in the served HTML / the phantom <title>
  // the shell's `document.title = …` bootstrap script leaves behind. After render,
  // collapse each set to a single element with the correct value.
  useEffect(() => {
    const keepLast = (selector: string, group: (el: Element) => string) => {
      const winners = new Map<string, Element>()
      document.querySelectorAll(selector).forEach((el) => winners.set(group(el), el))
      document.querySelectorAll(selector).forEach((el) => {
        if (winners.get(group(el)) !== el) el.remove()
      })
    }
    // <title> can't use keepLast: the phantom shell title is the LAST one, while
    // the page's own title is first — so keep the one matching `title` instead.
    dedupeTitleTags(document, title)
    keepLast('link[rel="canonical"]', () => 'canonical')
    keepLast('link[rel="alternate"]', (el) => el.getAttribute('hreflang') ?? '')
  }, [title, selfUrl, localizedUrls.en, localizedUrls.he, localizedUrls.de])

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={config.robots ?? 'index,follow'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={config.ogType ?? 'website'} />
      <meta property="og:url" content={selfUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map((altLocale) => (
        <meta key={altLocale} property="og:locale:alternate" content={toOpenGraphLocale(altLocale)} />
      ))}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <link rel="canonical" href={selfUrl} />
      <link rel="alternate" hrefLang="x-default" href={localizedUrls.en} />
      <link rel="alternate" hrefLang="en" href={localizedUrls.en} />
      {!enOnly && <link rel="alternate" hrefLang="he" href={localizedUrls.he} />}
      {!enOnly && <link rel="alternate" hrefLang="de" href={localizedUrls.de} />}
      <StructuredDataScript id="public-site-structured-data" data={sharedStructuredData} />
      {pageSchema ? (
        <StructuredDataScript id={`page-schema-${pageId}`} data={pageSchema} />
      ) : null}
    </>
  )
}

export function StructuredDataScript({
  data,
  id,
}: {
  data: Record<string, unknown>
  id?: string
}) {
  return (
    <script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  )
}

export function withPublicPageSeo<P extends object>(
  pageId: PublicPageSeoId,
  Component: ComponentType<P>
) {
  const WrappedComponent = (props: P) => (
    <>
      <PublicPageSeo pageId={pageId} />
      <Component {...props} />
    </>
  )

  WrappedComponent.displayName = `WithPublicPageSeo(${Component.displayName ?? Component.name ?? 'Component'})`

  return WrappedComponent
}
